import React, { useEffect, useState, useMemo, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, useTheme, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getProducts, deleteProduct } from '../../../redux/slice/products.slice';
import { useNavigate } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import useMediaQuery from '@mui/material/useMediaQuery';

const ProductDataGrid = memo(({ data, columns }) => {
    if (!data) {
        return <div>No data available</div>;
    }

    if (!Array.isArray(data)) {
        console.error('Data is not an array:', data);
        return <div>Invalid data format</div>;
    }
    const validRows = data.filter(row => row && row._id);

    if (validRows.length === 0) {
        return <div>No valid rows found</div>;
    }

    return (
        <DataGrid
            rows={validRows}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            getRowId={(row) => row._id || row.id || validRows.indexOf(row)}
            density="compact"
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
            }}
            initialState={{
                sorting: {
                    sortModel: [
                        { field: "Sr no", sort: "desc" }  // 👈 default sorting here
                    ],
                },
            }}
        />
    );
});

const ListProducts = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);
    const [open, setOpen] = useState(false);
    const [update, setUpdate] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [productDelete, setProductDelete] = useState(null);
    const navigate = useNavigate();
    const products = useSelector(state => state.products.products);
    const deleteStatus = useSelector(state => state.products.deleteStatus);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery('(max-width:500px)');

    useEffect(() => {
        dispatch(getProducts());
    }, [dispatch]);

    const handleClickOpenView = () => {
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
    };

    const handleDelete = (id) => {
        setDeleteConfirmOpen(true);
        setProductDelete(id);
    };
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setProductDelete(null);
    }

    const confirmDelete = () => {
        if (productDelete) {
            dispatch(deleteProduct(productDelete));
            setProductDelete(null);
            setDeleteConfirmOpen(false);
        }
    }

    const handleEdit = (data) => {
        console.log(data);
        setOpen(true);
        setUpdate(data);
        navigate('/admin/add-product', { state: data });
    };

    const handleView = (data) => {
        handleClickOpenView();
        setViewData(data)
    }

    const columns = useMemo(() => [
        {
            field: 'Sr no',
            headerName: 'Sr No',
            width: 80,
            valueGetter: (params) => {
                const index = params.api.getAllRowIds().indexOf(params.row._id);
                return index + 1;
            }
        },
        {
            field: 'mainCategory', headerName: 'Category', width: 180,
            filterable: true,
            valueGetter: (params) => {
                return params.row.mainCategoryData?.[0]?.categoryName;
            },
            renderCell: (params) => {
                return params.row.mainCategoryData?.[0]?.categoryName;
            }
        },
        {
            field: 'subCategory', headerName: 'Subcategory', width: 200,
            filterable: true,
            valueGetter: (params) => {
                return params.row.subCategoryData?.[0]?.subCategoryName;
            },

            renderCell: (params) => {
                return params.row.subCategoryData?.[0]?.subCategoryName;
            }
        },
        { field: 'productName', headerName: 'Product Name', width: 300 },
        { field: 'make', headerName: 'Make', width: 200 },
        { field: 'specifiaction', headerName: 'Specification ', width: 300 },
        { field: 'Size', headerName: 'Size', width: 140 },
        { field: 'Color', headerName: 'Color', width: 100 },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton aria-label="edit" onClick={() => handleView(params.row)}>
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    {deleteStatus && (
                        <IconButton aria-label="delete" onClick={() => handleDelete(params.row?._id)}>
                            <DeleteIcon />
                        </IconButton>
                    )}
                </>
            )
        }
    ], [deleteStatus]);


    const handleAddProduct = () => {
        navigate('/admin/add-product');
    }

    return (
        <Box m="2px">
            <Header
                title="PRODUCTS"
                subtitle="Manage Your Products Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddProduct}>
                    Add Product
                </Button>
            </Box>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },

                }), [colors])}
            >
                {products && products.length > 0 ? (
                    <ProductDataGrid data={products} columns={columns} />
                ) : (
                    <div>No products found</div>
                )}
            </Box>

            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: 'center', fontWeight: 'bold', fontSize: '18px' }}>
                    {"Product Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple, fontSize: '20px' }}>
                    <DialogContentText component="div"  >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>

                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }} ><strong>Category: </strong> {(viewData?.mainCategoryData?.[0]?.categoryName) || 'N/A'}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Subcategory: </strong> {(viewData?.subCategoryData?.[0]?.subCategoryName) || 'N/A'}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Product Name: </strong> {viewData?.productName}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Measurement: </strong> {viewData?.unitOfMeasurement}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>For: </strong> {viewData?.productFor}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Make: </strong> {viewData?.make}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Size: </strong> {viewData?.Size}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Specification: </strong> {viewData?.specifiaction}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Color: </strong> {viewData?.Color}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Description: </strong> {viewData?.Desacription}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>HSN Code: </strong> {viewData?.HSNcode}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Tax: </strong> {viewData?.taxdetails}</p>
                            <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}><strong>Warranty: </strong> {viewData?.Warrentry}</p>

                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this product?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListProducts;

